import i18n from 'i18n-js';

import type {
  JourneyCampaignSyncPreviewCountry,
  SheetSyncCampaignRef,
  SheetSyncPreviewResponse,
} from 'omega-rtc/types/ts/journey-sheet-sync';

import { getJourneyCampaignDisplayStatus } from './journey-campaign-status';

type Country = { countryID: number | string; code: string; name: string };

// Match the reviewed selection, including country, rather than UCD's campaign/project lookup key.
const hasSameSheetSyncCampaignIdentity = (
  left: SheetSyncCampaignRef,
  right: SheetSyncCampaignRef,
): boolean => left.campaignID === right.campaignID
  && left.projectID === right.projectID
  && String(left.countryID) === String(right.countryID);

export const getCurrentJourneyCampaignSheetSyncCampaigns = (
  selectedCampaigns: SheetSyncCampaignRef[],
  currentCampaigns: SheetSyncCampaignRef[],
): SheetSyncCampaignRef[] => selectedCampaigns.map((selected) => (
  currentCampaigns.find((current) => hasSameSheetSyncCampaignIdentity(current, selected))
  || { ...selected, status: '', journeyMetadata: undefined, isMissingFromCurrentGroup: true }
));

// Only a token for exactly the currently reviewed campaigns may be applied.
export const isSheetSyncPreviewForCampaigns = (
  preview: SheetSyncPreviewResponse | undefined,
  campaigns: SheetSyncCampaignRef[],
): boolean => {
  if (!preview || !preview.previewToken || !campaigns.length || !preview.campaigns
    || preview.campaigns.length !== campaigns.length) return false;
  return campaigns.every((campaign) => preview.campaigns.some((result) => (
    hasSameSheetSyncCampaignIdentity(result, campaign)
  )));
};

export const buildJourneyCampaignSyncPreview = (
  preview: SheetSyncPreviewResponse | undefined,
  campaigns: SheetSyncCampaignRef[],
  countries: Country[] = [],
): JourneyCampaignSyncPreviewCountry[] => ((preview && preview.campaigns) || []).map((result) => {
  const campaign = campaigns.find((candidate) => hasSameSheetSyncCampaignIdentity(candidate, result));
  const country = countries.find((candidate) => (
    String(candidate.countryID) === String(result.countryID)
  ));
  const countryName = (campaign && campaign.countryName) || (country && country.name) || `Country ${result.countryID}`;
  return {
    campaignID: result.campaignID,
    projectID: result.projectID,
    code: (campaign && campaign.countryCode) || (country && country.code) || '',
    country: countryName,
    countryID: result.countryID,
    syncBlockedReason: campaign && getJourneyCampaignSheetSyncBlockedReason(campaign, result.sheetGroups),
    sheetGroups: (result.sheetGroups || []).map((group) => ({
      groupID: group.groupID,
      channel: group.channel,
      controlGroupPercentage: (group.experiment && group.experiment.controlGroupPercentage) || 0,
      variants: (group.variants || []).map((variant) => ({
        ...variant,
        criteria: variant.criteria || {},
      })),
    })),
    datalakeGroups: (result.datalakeGroups || []).map((group) => ({
      country: countryName,
      groupID: group.groupID,
      paxCount: Number(group.paxCount || 0),
    })),
  };
});

// Metadata groups are persisted on successful sync; Datalake membership is not
// a record of the last synchronized campaign. Never substitute variants/hashes.
export const getJourneyCampaignSheetSyncBlockedReason = (
  campaign: SheetSyncCampaignRef,
  sheetGroups: Array<{ groupID: string }> | undefined,
): string | undefined => {
  if (campaign.isMissingFromCurrentGroup) {
    return i18n.t('journey_campaign_sheet_sync.missing_current_campaign', {
      defaultValue: 'The selected campaign is no longer present in the current campaign group. Refresh the campaign group before syncing.',
    });
  }
  const metadata = campaign.journeyMetadata || {};
  if ((metadata.syncStatus || '').toUpperCase() === 'IN_PROGRESS') {
    return i18n.t('journey_campaign_sheet_sync.in_progress', {
      defaultValue: 'A sheet sync is already in progress.',
    });
  }
  const status = getJourneyCampaignDisplayStatus(campaign);
  if (status === 'DRAFT') return undefined;
  if (status !== 'ACTIVE') {
    return i18n.t('journey_campaign_sheet_sync.status_blocked', {
      defaultValue: 'Only Draft or eligible Active campaigns can sync from Sheet.',
    });
  }
  const syncedGroups = metadata.groups;
  const hasValidIDs = (groups: Array<{ groupID: string }> | undefined): boolean => (
    Array.isArray(groups) && groups.every((group) => (
      group && typeof group.groupID === 'string' && group.groupID.trim().length > 0
    ))
  );
  if (!metadata.lastSyncedAt || !syncedGroups || !sheetGroups
    || !hasValidIDs(syncedGroups) || !hasValidIDs(sheetGroups)) {
    return i18n.t('journey_campaign_sheet_sync.comparison_unavailable', {
      defaultValue: 'Active campaign sync eligibility cannot be established because the current sheet or last successfully synchronized group IDs are unavailable.',
    });
  }
  // Group IDs are case-sensitive, matching the backend set comparison and variant keys.
  const previousIDs = new Set(syncedGroups.map((group) => group.groupID));
  const currentIDs = new Set(sheetGroups.map((group) => group.groupID));
  if (previousIDs.size !== currentIDs.size
    || Array.from(previousIDs).some((ID) => !currentIDs.has(ID))) {
    return i18n.t('journey_campaign_sheet_sync.group_ids_changed', {
      defaultValue: 'Active campaign sync is blocked because group IDs changed since the last successful sync. Restore the same group-ID set before syncing.',
    });
  }
  return undefined;
};

export const canApplyJourneyCampaignSheetSync = (
  preview: SheetSyncPreviewResponse | undefined,
  campaigns: SheetSyncCampaignRef[],
): boolean => {
  if (!preview || !isSheetSyncPreviewForCampaigns(preview, campaigns)) return false;
  return campaigns.every((campaign) => {
    const result = preview.campaigns.find((candidate) => hasSameSheetSyncCampaignIdentity(candidate, campaign));
    return !getJourneyCampaignSheetSyncBlockedReason(campaign, result && result.sheetGroups);
  });
};

export const getJourneyCampaignSheetSyncErrorMessage = (error: any): string | undefined => {
  const data = error && error.response && error.response.data;
  if (!data) return undefined;
  const messages = Array.isArray(data.errors)
    ? data.errors.map((item) => item && item.message).filter((message) => typeof message === 'string') : [];
  if (messages.length) return messages.join(' ');
  return typeof data.message === 'string' ? data.message : undefined;
};
